import { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { useColorScheme } from '@mui/material/styles';
import { ArcElement, Chart as ChartJS } from 'chart.js';
import { isNilOrEmpty } from 'ramda-extension';
import { Doughnut } from 'react-chartjs-2';

import { generateRemainingHoursColor } from 'features/views/marketPlace/utils';

ChartJS.register(ArcElement);

export const RemainingTimeDoughnutChart = ({ remainingTimeData }) => {
	const chartRef = useRef(null);

	const { totalHours, remainingHours } = remainingTimeData;

	const { mode: theme } = useColorScheme();
	const remainingHoursOrZero = remainingHours < 0 ? 0 : remainingHours;

	const generatePlugins = useMemo(
		() => [
			{
				id: 'remainingTimeDoughnutChart',
				beforeDraw(chart) {
					const { width } = chart;
					const { height } = chart;
					const { ctx } = chart;
					ctx.restore();
					const fontSize = (height / 5).toFixed(2);
					ctx.font = `${fontSize}px sans-serif`;
					ctx.fillStyle = theme === 'light' ? 'black' : 'white';
					const text = `${remainingHoursOrZero}h`;
					const textX = Math.round((width - ctx.measureText(text).width) / 2);
					const textY = height / 2 + 4;
					ctx.fillText(text, textX, textY);
					ctx.save();
				},
			},
		],
		[theme, remainingHoursOrZero],
	);

	if (isNilOrEmpty(totalHours) || isNilOrEmpty(remainingHours)) {
		return null;
	}

	const remainingHoursColor = generateRemainingHoursColor({ totalHours, remainingHours: remainingHoursOrZero });

	const passedPeriod = totalHours - remainingHoursOrZero;
	const passedPercentageValue = (passedPeriod / totalHours) * 100;
	const remainingTimePercentage = Math.floor(100 - passedPercentageValue);
	const orderedData = [passedPercentageValue, remainingTimePercentage];

	const data = {
		datasets: [
			{
				data: orderedData,
				backgroundColor: ['#EEEEEE', remainingHoursColor],
				borderRadius: 10,
				borderWidth: 1,
			},
		],
	};

	const options = {
		cutout: '75%',
		maintainAspectRatio: false,
		hover: { mode: null },
		plugins: {
			tooltip: {
				enabled: false,
			},
		},
	};

	return (
		<Box sx={{ height: '50px', width: '50px' }}>
			<Doughnut ref={chartRef} data={data} options={options} redraw plugins={generatePlugins} />
		</Box>
	);
};

RemainingTimeDoughnutChart.propTypes = {
	remainingTimeData: PropTypes.shape({
		totalHours: PropTypes.number,
		remainingHours: PropTypes.number,
	}),
};

export default RemainingTimeDoughnutChart;
